'use client';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SearchField from "src/components/form/SearchField";
import { Filter } from "lucide-react";
import { cn } from "src/lib/utils";
import StatusBadge from "./StatusBadge";

interface FiltersProps {
    searchTerm: string;
    setSearchTerm: (searchTerm: string) => void;
    selectedCategory?: string;
    setSelectedCategory?: (selectedCategory: string) => void;
    sortBy?: string;
    setSortBy?: (sortBy: string) => void;
    categories?: string[];
    sortByOptions?: string[];
    filterInfo?: string;
    showFilterInfo?: boolean;
    className?: string;
    placeholder?: string;
}

export default function Filters({
    searchTerm, 
    setSearchTerm,
    selectedCategory,
    setSelectedCategory,
    sortBy, setSortBy,
    categories,
    sortByOptions,
    filterInfo,
    showFilterInfo = false,
    placeholder = "Search",
    className
}: FiltersProps) {
    return (
        <div className={cn("bg-white rounded-xl border p-4 lg:p-6", className)}>
            <div className="flex flex-col flex-wrap lg:flex-row gap-2 lg:gap-4 items-center justify-between">
                <div className="flex flex-col items-center lg:flex-row gap-2 lg:gap-4 w-full lg:w-auto">
                    <SearchField
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        placeholder={placeholder}
                        className="flex-1 w-full lg:w-[400px]"
                    />

                    {/* Category Filter */}
                    {categories && (
                        <div className="relative w-full lg:w-auto">
                            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary-400 w-4 h-4" />
                            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                            <SelectTrigger className="pl-10 min-w-[160px] w-full lg:w-auto">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="capitalize">
                                <SelectItem value="all">All Categories</SelectItem>
                                {categories.slice(1).map(category => (
                                <SelectItem 
                                    key={category} 
                                    value={category}
                                >
                                    {category}
                                </SelectItem>
                                ))}
                            </SelectContent>
                            </Select>
                        </div>
                    )}

                    {/* Sort Filter */}
                    {sortByOptions && (
                        <Select value={sortBy} onValueChange={setSortBy}>
                            <SelectTrigger className="min-w-[140px] w-full lg:w-auto">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                            {sortByOptions.map(option => (
                                <SelectItem key={option} value={option}>{option}</SelectItem>
                            ))}
                            </SelectContent>
                        </Select>
                    )}
                </div>

                {/* filter info */}
                {filterInfo && (
                    <div className="text-sm text-gray-500">
                        {filterInfo}
                    </div>
                )}
            </div>

            {/* filter info */}
            {showFilterInfo && (searchTerm || selectedCategory !== "All") && (
                <div className="flex flex-wrap gap-2 mt-4">
                    {searchTerm && (
                        <StatusBadge
                            title={`Search: "${searchTerm}"`}
                            onClear={() => setSearchTerm("")}
                            variant="primary"
                        />
                    )}
                    {selectedCategory !== "All" && (
                        <StatusBadge
                            title={`Category: ${selectedCategory}`}
                            onClear={() => setSelectedCategory?.("All")}
                            variant="primary"
                        />
                    )}
                </div>
            )}
        </div>
    )
}